/**
 * Retro Terminal Theme Styles
 * 
 * Estilos específicos para o tema Terminal Retrô.
 * Características: estética DOS/Unix, fonte monospace, cores fosforescentes, scanlines.
 */

import { CSSProperties } from 'react';

// ============================================
// BASE STYLES
// ============================================

export const retroTerminalBase: CSSProperties = {
  background: 'var(--term-bg)',
  color: 'var(--term-text)',
  fontFamily: 'var(--term-font)',
  fontSize: 'var(--term-font-size)',
  lineHeight: 'var(--term-line-height)',
  letterSpacing: 'var(--term-letter-spacing)',
  textShadow: 'var(--term-text-shadow)',
  transition: 'var(--term-transition)',
};

// ============================================
// INPUT STYLES
// ============================================

export const retroTerminalInput: CSSProperties = {
  background: 'var(--term-bg-secondary)',
  color: 'var(--term-text)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  padding: '12px 16px',
  fontSize: 'var(--term-font-size)',
  fontFamily: 'var(--term-font)',
  letterSpacing: 'var(--term-letter-spacing)',
  textShadow: 'var(--term-text-shadow)',
  boxShadow: 'var(--term-glow-sm)',
  transition: 'var(--term-transition-glow)',
  outline: 'none',
  caretColor: 'var(--term-text)',
};

export const retroTerminalInputFocus: CSSProperties = {
  boxShadow: 'var(--term-glow)',
  borderColor: 'var(--term-text)',
};

export const retroTerminalInputError: CSSProperties = {
  color: '#ff4444',
  borderColor: '#ff4444',
  textShadow: '0 0 10px #ff4444',
  boxShadow: '0 0 10px rgba(255, 68, 68, 0.5)',
};

export const retroTerminalInputDisabled: CSSProperties = {
  opacity: 0.4,
  cursor: 'not-allowed',
  color: 'var(--term-text-dim)',
};

export const retroTerminalInputWithCursor: CSSProperties = {
  position: 'relative',
};

// Pseudo-elemento para cursor piscando (aplicar via CSS)
export const retroTerminalCursor: CSSProperties = {
  display: 'inline-block',
  width: 'var(--term-cursor-width)',
  height: 'var(--term-cursor-height)',
  background: 'var(--term-text)',
  animation: 'var(--term-cursor-blink)',
  marginLeft: '2px',
  verticalAlign: 'middle',
};

// ============================================
// BUTTON STYLES
// ============================================

export const retroTerminalButton: CSSProperties = {
  background: 'transparent',
  color: 'var(--term-text)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  padding: '10px 20px',
  fontSize: 'var(--term-font-size)',
  fontFamily: 'var(--term-font)',
  letterSpacing: 'var(--term-letter-spacing)',
  textShadow: 'var(--term-text-shadow)',
  cursor: 'pointer',
  transition: 'var(--term-transition-glow)',
  outline: 'none',
  textTransform: 'uppercase',
};

export const retroTerminalButtonHover: CSSProperties = {
  background: 'var(--term-text)',
  color: 'var(--term-bg)',
  boxShadow: 'var(--term-glow)',
  textShadow: 'none',
};

export const retroTerminalButtonActive: CSSProperties = {
  background: 'var(--term-text)',
  color: 'var(--term-bg)',
  boxShadow: 'var(--term-glow-lg)',
};

export const retroTerminalButtonPrimary: CSSProperties = {
  background: 'var(--term-text)',
  color: 'var(--term-bg)',
  boxShadow: 'var(--term-glow-sm)',
  textShadow: 'none',
};

export const retroTerminalButtonDanger: CSSProperties = {
  color: '#ff4444',
  borderColor: '#ff4444',
  textShadow: '0 0 10px #ff4444',
};

export const retroTerminalButtonDisabled: CSSProperties = {
  opacity: 0.4,
  cursor: 'not-allowed',
  color: 'var(--term-text-dim)',
  borderColor: 'var(--term-text-dim)',
};

// ============================================
// CARD STYLES
// ============================================

export const retroTerminalCard: CSSProperties = {
  background: 'var(--term-bg-card)',
  color: 'var(--term-text)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  padding: 'var(--term-spacing-lg)',
  fontFamily: 'var(--term-font)',
  textShadow: 'var(--term-text-shadow)',
  position: 'relative',
  overflow: 'hidden',
};

export const retroTerminalCardWithScanlines: CSSProperties = {
  ...retroTerminalCard,
  // Scanlines são aplicadas via pseudo-elemento ::after
};

export const retroTerminalCardHeader: CSSProperties = {
  borderBottom: 'var(--term-border-dim)',
  paddingBottom: 'var(--term-spacing-md)',
  marginBottom: 'var(--term-spacing-md)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

export const retroTerminalCardTitle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const retroTerminalCardTitlePrefix: CSSProperties = {
  color: 'var(--term-text-dim)',
};

// ============================================
// SELECT STYLES
// ============================================

export const retroTerminalSelect: CSSProperties = {
  background: 'var(--term-bg-secondary)',
  color: 'var(--term-text)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  padding: '12px 40px 12px 16px',
  fontSize: 'var(--term-font-size)',
  fontFamily: 'var(--term-font)',
  letterSpacing: 'var(--term-letter-spacing)',
  textShadow: 'var(--term-text-shadow)',
  cursor: 'pointer',
  transition: 'var(--term-transition-glow)',
  appearance: 'none',
};

export const retroTerminalSelectDropdown: CSSProperties = {
  background: 'var(--term-bg)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  boxShadow: 'var(--term-glow)',
  marginTop: '4px',
  fontFamily: 'var(--term-font)',
};

export const retroTerminalSelectOption: CSSProperties = {
  padding: '10px 16px',
  cursor: 'pointer',
  borderBottom: 'var(--term-border-dim)',
  transition: 'var(--term-transition)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const retroTerminalSelectOptionHover: CSSProperties = {
  background: 'var(--term-text)',
  color: 'var(--term-bg)',
  textShadow: 'none',
};

export const retroTerminalSelectOptionPrefix: CSSProperties = {
  color: 'var(--term-text-dim)',
};

// ============================================
// TABLE STYLES
// ============================================

export const retroTerminalTable: CSSProperties = {
  background: 'var(--term-bg)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  fontFamily: 'var(--term-font)',
  fontSize: 'var(--term-font-size)',
  textShadow: 'var(--term-text-shadow)',
  width: '100%',
  borderCollapse: 'collapse',
};

export const retroTerminalTableHeader: CSSProperties = {
  background: 'var(--term-bg-secondary)',
  color: 'var(--term-text)',
  padding: '12px 16px',
  textAlign: 'left',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  borderBottom: 'var(--term-border)',
};

export const retroTerminalTableRow: CSSProperties = {
  borderBottom: 'var(--term-border-dim)',
  transition: 'var(--term-transition)',
};

export const retroTerminalTableRowHover: CSSProperties = {
  background: 'var(--term-bg-secondary)',
  boxShadow: 'inset 0 0 10px rgba(0, 255, 0, 0.1)',
};

export const retroTerminalTableCell: CSSProperties = {
  padding: '12px 16px',
  color: 'var(--term-text)',
  borderRight: 'var(--term-border-dim)',
};

export const retroTerminalTableCellLast: CSSProperties = {
  borderRight: 'none',
};

// ============================================
// MODAL STYLES
// ============================================

export const retroTerminalModalOverlay: CSSProperties = {
  background: 'rgba(0, 0, 0, 0.9)',
};

export const retroTerminalModal: CSSProperties = {
  background: 'var(--term-bg)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  boxShadow: 'var(--term-glow-lg)',
  padding: 'var(--term-spacing-lg)',
  fontFamily: 'var(--term-font)',
  color: 'var(--term-text)',
  textShadow: 'var(--term-text-shadow)',
  position: 'relative',
};

export const retroTerminalModalHeader: CSSProperties = {
  borderBottom: 'var(--term-border)',
  paddingBottom: 'var(--term-spacing-md)',
  marginBottom: 'var(--term-spacing-lg)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const retroTerminalModalTitle: CSSProperties = {
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
};

export const retroTerminalModalTitlePrefix: CSSProperties = {
  color: 'var(--term-text-dim)',
};

// ============================================
// TABS STYLES
// ============================================

export const retroTerminalTabs: CSSProperties = {
  display: 'flex',
  borderBottom: 'var(--term-border)',
  fontFamily: 'var(--term-font)',
  gap: '0',
};

export const retroTerminalTab: CSSProperties = {
  padding: '10px 20px',
  background: 'transparent',
  color: 'var(--term-text-dim)',
  border: 'var(--term-border-dim)',
  borderBottom: 'none',
  borderRadius: 'var(--term-radius)',
  cursor: 'pointer',
  fontFamily: 'var(--term-font)',
  fontSize: 'var(--term-font-size)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  transition: 'var(--term-transition-glow)',
  marginRight: '-1px',
};

export const retroTerminalTabActive: CSSProperties = {
  color: 'var(--term-text)',
  background: 'var(--term-bg-secondary)',
  borderColor: 'var(--term-text)',
  textShadow: 'var(--term-text-shadow)',
  boxShadow: 'var(--term-glow-sm)',
};

export const retroTerminalTabHover: CSSProperties = {
  color: 'var(--term-text)',
  textShadow: 'var(--term-text-shadow)',
};

// ============================================
// BADGE STYLES
// ============================================

export const retroTerminalBadge: CSSProperties = {
  background: 'transparent',
  color: 'var(--term-text)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  padding: '4px 10px',
  fontSize: '11px',
  fontFamily: 'var(--term-font)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  textShadow: 'var(--term-text-shadow)',
};

export const retroTerminalBadgeSuccess: CSSProperties = {
  color: '#00ff00',
  borderColor: '#00ff00',
  textShadow: '0 0 10px #00ff00',
};

export const retroTerminalBadgeWarning: CSSProperties = {
  color: '#ffff00',
  borderColor: '#ffff00',
  textShadow: '0 0 10px #ffff00',
};

export const retroTerminalBadgeDanger: CSSProperties = {
  color: '#ff4444',
  borderColor: '#ff4444',
  textShadow: '0 0 10px #ff4444',
};

export const retroTerminalBadgeInfo: CSSProperties = {
  color: '#00ffff',
  borderColor: '#00ffff',
  textShadow: '0 0 10px #00ffff',
};

// ============================================
// TOOLTIP STYLES
// ============================================

export const retroTerminalTooltip: CSSProperties = {
  background: 'var(--term-bg)',
  color: 'var(--term-text)',
  border: 'var(--term-border)',
  borderRadius: 'var(--term-radius)',
  padding: '8px 12px',
  fontSize: '12px',
  fontFamily: 'var(--term-font)',
  textShadow: 'var(--term-text-shadow)',
  boxShadow: 'var(--term-glow)',
};

// ============================================
// SPECIAL EFFECTS
// ============================================

export const retroTerminalScanlines: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--term-scanline)',
  pointerEvents: 'none',
  zIndex: 1,
};

export const retroTerminalFlicker: CSSProperties = {
  animation: 'var(--term-flicker)',
};

export const retroTerminalGlowPulse: CSSProperties = {
  animation: 'var(--term-glow-pulse)',
};

// ============================================
// PROMPT STYLES
// ============================================

export const retroTerminalPrompt: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'var(--term-font)',
  color: 'var(--term-text)',
};

export const retroTerminalPromptSymbol: CSSProperties = {
  color: 'var(--term-text)',
  fontWeight: 'bold',
};

export const retroTerminalPromptPath: CSSProperties = {
  color: 'var(--term-text-dim)',
};

